<script lang="ts">
import { defineComponent, PropType } from "vue";
import { area, axisBottom, axisRight, extent, scaleTime, select } from "d3";
import { scaleLinear } from "d3-scale";
import i18n from "@/core/i18n";
import { getDateFormatFunction } from "@/features/chart/d3-locale";

export type ChartPoint = { D: number; K: number; V: number };

const adjustExtent = (
  extent: [number, number],
  minExtent: number,
  minOrigin: number
): [number, number] => {
  const e = extent[1] - extent[0];
  if (e < minExtent) {
    return [
      Math.min(extent[0] - minExtent / 2, minOrigin),
      extent[1] + minExtent / 2,
    ];
  } else {
    return [Math.min(minOrigin, extent[0]), extent[1]];
  }
};

export default defineComponent({
  props: {
    points: {
      type: Array as PropType<ChartPoint[]>,
      required: true,
    },
    showFiltrationOff: {
      type: Boolean,
      required: true,
    },
    minExtent: {
      type: Number,
      default: 0,
    },
    minOrigin: {
      type: Number,
      default: Infinity,
    },
  },
  mounted() {
    const svg = select(this.$refs.svg as SVGElement);
    const { width, height } = svg.node()?.getBoundingClientRect() || {
      width: 0,
      height: 0,
    };

    const xExtent = extent(this.points, (p) => p.D * 1000) as [number, number];
    const yExtent = adjustExtent(
      extent(this.points, (p) => p.V) as [number, number],
      this.minExtent,
      this.minOrigin
    );

    // Split into segments based on K
    const { lines } = this.points.reduce(
      ({ lines, line }, point, i) => {
        if (!line.length) {
          return { line: [point], lines };
        } else if (point.K !== line.slice(-1)[0].K) {
          return {
            line: [point],
            lines: [...lines, [...line, point]],
          };
        } else if (i === this.points.length - 1) {
          return {
            lines: [...lines, [...line, point]],
            line: [],
          };
        } else {
          return {
            line: [...line, point],
            lines,
          };
        }
      },
      { line: [] as ChartPoint[], lines: [] as ChartPoint[][] }
    );

    const y = scaleLinear()
      .domain(yExtent)
      .range([height - 50, 0]);
    const x = scaleTime()
      .range([0, width - 50])
      .domain(xExtent);

    svg
      .append("g")
      .attr("class", "axis y")
      .attr("transform", `translate(${width - 30}, 25)`)
      .call(
        axisRight(y)
          .ticks(5)
          .tickSizeOuter(0)
          .tickSizeInner(-width)
          .tickPadding(10)
      );

    svg
      .append("g")
      .attr("class", "lines")
      .selectAll("path")
      .data(lines)
      .enter()
      .append("path")
      .datum((line) => line)
      .attr(
        "d",
        area<ChartPoint>()
          .x((d) => x(d.D * 1000))
          .y0(yExtent[0] > 0 ? y(yExtent[0]) + 25 : y(0) + 25)
          .y1((d) => y(d.V) + 25)
      )
      .attr("fill", (line) =>
        line[0].K ? "#B5ECFF" : this.showFiltrationOff ? "#9ca3af" : "none"
      )
      .attr("fill-opacity", 0.9);

    svg
      .append("g")
      .attr("class", "axis x")
      .attr("transform", `translate(0, ${height - 25})`)
      .call(
        axisBottom<Date>(x)
          .tickFormat(
            getDateFormatFunction(i18n.global.locale as "en" | "fr" | "de")
          )
          .tickSize(0)
          .tickPadding(10)
      );
  },
});
</script>

<template>
  <svg ref="svg" class="w-full" style="height: 200px"></svg>
</template>

<style>
svg .axis path {
  display: none;
}

svg .axis line {
  stroke: white;
  stroke-width: 0.2px;
}
svg .axis text {
  fill: white;
}
</style>
